import { FC, useContext, useEffect } from 'react'

import { Option } from '@joor/design-system'
import { isEqual } from 'lodash'
import { useSelector } from 'react-redux'

import { isLiteRetailer as isLiteRetailerSelector } from 'store/currentUser/selectors'

import { SimplifiedTypes } from '../FirstStep/FirstStep.utils'
import { ImportOrdersContext } from '../ImportOrders.context'
import { Warehouse } from '../ImportOrders.types'
import { ImportOrdersStandardContext } from '../ImportOrdersStandard.context'
import {
  formatDropdownOptions,
  useConnectedAccounts,
  useRetailerBrandDoors,
  useSecondStepSharedData,
} from './SecondStep.hooks'
import SecondStepStandard from './SecondStepStandard/SecondStepStandard'
import SecondStepVerticalHorizontal from './SecondStepVerticalHorizontal/SecondStepVerticalHorizontal'

const SecondStep: FC = () => {
  const {
    brand,
    brandWarehousesOptions,
    connectedAccountsOptions,
    doorsOptions,
    templateType,
    includeDoor,
    includedRetailer,
    isLoadingConnectedAccounts,
    isLoadingSecondStepSharedData,
    orderTypeOptions,
    retailer,
    retailerSearchText,
    retailerWarehousesOptions,
    warehouse,
    setBrandWarehousesOptions,
    setConnectedAccountsOptions,
    setDoor,
    setDoorsOptions,
    setIsLoadingConnectedAccounts,
    setIsLoadingSecondStepSharedData,
    setOrderTypeOptions,
    setRetailer,
    setRetailerSearchText,
    setRetailerWarehousesOptions,
    setWarehouse,
    setModalTitle,
  } = useContext(ImportOrdersContext)

  const {
    includedRetailerFound,
    setIncludedRetailerFound,
    setDisableRetailerDropdown,
  } = useContext(ImportOrdersStandardContext)
  const isLiteRetailer = useSelector(isLiteRetailerSelector) as boolean

  const {
    loading,
    orderTypes,
    brandDoors,
    brandWarehouses,
    retailerWarehouses,
  } = useSecondStepSharedData(isLiteRetailer, retailer?.value)

  const { loading: loadingRetailerBrandDoors, doors: retailerBrandDoors } =
    useRetailerBrandDoors(includeDoor, isLiteRetailer, brand?.value)

  const {
    connectedAccountsOptions: formattedConnectedAccounts,
    connectedAccountsQueryLoading,
  } = useConnectedAccounts(retailerSearchText)

  useEffect(() => setModalTitle('Order Settings'), [setModalTitle])

  useEffect(() => {
    if (isLoadingConnectedAccounts !== connectedAccountsQueryLoading) {
      setIsLoadingConnectedAccounts(connectedAccountsQueryLoading)
    }
  }, [
    connectedAccountsQueryLoading,
    isLoadingConnectedAccounts,
    setIsLoadingConnectedAccounts,
  ])

  useEffect(() => {
    if (!isEqual(connectedAccountsOptions, formattedConnectedAccounts)) {
      setConnectedAccountsOptions(formattedConnectedAccounts)
    }
  }, [
    connectedAccountsOptions,
    formattedConnectedAccounts,
    setConnectedAccountsOptions,
  ])

  useEffect(() => {
    if (!!includedRetailer) setRetailerSearchText(includedRetailer)
  }, [includedRetailer, setRetailerSearchText])

  useEffect(() => {
    if (
      !includedRetailer ||
      includedRetailer !== retailerSearchText ||
      connectedAccountsQueryLoading ||
      includedRetailerFound !== undefined
    ) {
      return
    }

    if (formattedConnectedAccounts.length !== 1) {
      setIncludedRetailerFound(false)
      return setDisableRetailerDropdown(false)
    }

    if (!isEqual(retailer, formattedConnectedAccounts[0])) {
      setIncludedRetailerFound(true)
      setRetailer(formattedConnectedAccounts[0])
      return setDisableRetailerDropdown(true)
    }
  }, [
    connectedAccountsQueryLoading,
    formattedConnectedAccounts,
    includedRetailer,
    includedRetailerFound,
    retailer,
    retailerSearchText,
    setDisableRetailerDropdown,
    setIncludedRetailerFound,
    setRetailer,
  ])

  useEffect(() => {
    if (
      isLoadingSecondStepSharedData !== (loading || loadingRetailerBrandDoors)
    ) {
      setIsLoadingSecondStepSharedData(loading || loadingRetailerBrandDoors)
    }
  }, [loading, isLoadingSecondStepSharedData, setIsLoadingSecondStepSharedData])

  useEffect(() => {
    const formattedOrderTypes = formatDropdownOptions(orderTypes)
    if (!isEqual(orderTypeOptions, formattedOrderTypes)) {
      setOrderTypeOptions(formattedOrderTypes)
    }
  }, [orderTypes, orderTypeOptions, setOrderTypeOptions])

  useEffect(() => {
    const formattedDoors = formatDropdownOptions(
      isLiteRetailer ? retailerBrandDoors : brandDoors,
    )
    if (!isEqual(doorsOptions, formattedDoors)) {
      setDoorsOptions(formattedDoors)

      if (formattedDoors.length === 1) {
        setDoor(formattedDoors[0])
      }
    }
    // The isLiteRetailer flag should never change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandDoors, retailerBrandDoors, doorsOptions])

  useEffect(() => {
    const formattedBrandWarehouses = formatDropdownOptions(brandWarehouses)
    if (!isEqual(brandWarehousesOptions, formattedBrandWarehouses)) {
      setBrandWarehousesOptions(formattedBrandWarehouses)
    }
  }, [brandWarehouses, brandWarehousesOptions, setBrandWarehousesOptions])

  useEffect(() => {
    const formattedRetailerWarehouses =
      formatDropdownOptions(retailerWarehouses)
    if (!isEqual(retailerWarehousesOptions, formattedRetailerWarehouses)) {
      setRetailerWarehousesOptions(formattedRetailerWarehouses)
    }
  }, [
    retailerWarehouses,
    retailerWarehousesOptions,
    setRetailerWarehousesOptions,
  ])

  // Set default warehouse or first after loading retailer matched warehouses
  useEffect(() => {
    if (!retailer || isLoadingSecondStepSharedData) return

    const [warehouses, warehouseOptions] =
      retailerWarehouses.length > 0
        ? [retailerWarehouses, retailerWarehousesOptions]
        : [brandWarehouses, brandWarehousesOptions]

    const selectedWarehouse = warehouse
      ? warehouseOptions.find(
          (option: Option) => option.value === warehouse.value,
        )
      : undefined

    // If there's a selected warehouse, don't bother finding the default
    const defaultWarehouse = selectedWarehouse
      ? undefined
      : warehouses.find((warehouse: Warehouse) => warehouse.default)
    const defaultWarehouseOption = defaultWarehouse
      ? warehouseOptions.find((option) => option.value === defaultWarehouse.id)
      : undefined

    setWarehouse(
      selectedWarehouse || defaultWarehouseOption || warehouseOptions[0],
    )
  }, [
    retailer,
    isLoadingSecondStepSharedData,
    warehouse,
    brandWarehouses,
    retailerWarehouses,
    brandWarehousesOptions,
    retailerWarehousesOptions,
    setWarehouse,
  ])

  return (
    <>
      {templateType?.value === SimplifiedTypes.Standard && (
        <SecondStepStandard />
      )}
      {[SimplifiedTypes.Vertical, SimplifiedTypes.Horizontal].includes(
        templateType?.value as SimplifiedTypes,
      ) && <SecondStepVerticalHorizontal />}
    </>
  )
}

export default SecondStep
